/**
 * IFCValidationPanel — BIM-manager-oriented validation dashboard.
 *
 * Replaces the prior dashboard chart component (confidence gauge + cert
 * pie + per-class confidence + keyword-derived category compliance),
 * none of which mapped to actions a BIM coordinator takes during
 * handover review.
 *
 * Sections:
 *   1) Failures by IFC class      — horizontal stacked bar (PASS/WARN/FAIL)
 *   2) Top failing rules          — horizontal bar, color-by-severity
 *   3) Compliance by discipline   — horizontal progress bars + lowest callout
 *   4) FM parameter gap matrix    — storey × FM-parameter heatmap (MEP only)
 *
 * Props (matches the prior contract — see ResultsPage.js:888):
 *   data:     camelCased AIM register rows. Each row has
 *             { ifcClass, overallStatus, storey, issues, ... }.
 *             ``issues`` is an array of strings.
 *   summary:  audit.summary blob — referenced for callouts only.
 *   auditId:  the audit UUID — used to fetch validation_results.
 *
 * Data sources (no new endpoints created):
 *   • ``data`` prop (already passed in) → Sections 1, 3, 4.
 *   • ``GET /api/audits/{auditId}/validation-details`` → Section 2.
 *     Existing endpoint at routes/audits_views.py:270. Returns columns
 *     `rule_id, rule_name, severity, ifc_class, status, field_checked`
 *     among others. Verified via supabase introspection.
 *
 * Pagination contract for Section 2 — the endpoint caps at 5000 rows
 * per call. We page in chunks of 5000 using the response's `has_more`
 * flag until exhausted; the loop is bounded at 20 pages (100k rows)
 * as a safety stop. Pages are accumulated in memory because we
 * aggregate by rule_id — a streaming pivot would add state for
 * negligible gain at the rule-count cardinality we see in practice.
 */
import { useMemo, useEffect, useState, useCallback } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { apiFetch } from '../lib/supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Authoritative discipline mapping — mirror of
// services/rule_engine.py:46 DISCIPLINE_MAPPING. Mirrored on the
// frontend so we don't add an endpoint just to fetch a static list.
// When backend DISCIPLINE_MAPPING grows, append here.
const DISCIPLINE_MAPPING = {
  Architecture: [
    'IfcWall', 'IfcWallStandardCase', 'IfcDoor', 'IfcWindow',
    'IfcStairFlight', 'IfcStair', 'IfcSlab', 'IfcRoof', 'IfcRailing',
    'IfcCovering', 'IfcCurtainWall', 'IfcBuildingElementPart',
    'IfcSpace', 'IfcSpatialZone',
  ],
  Structure: [
    'IfcBeam', 'IfcColumn', 'IfcPlate', 'IfcFooting', 'IfcPile',
    'IfcMember', 'IfcMechanicalFastener', 'IfcReinforcingBar',
    'IfcReinforcingMesh', 'IfcTendon',
  ],
  MEP: [
    'IfcFlowTerminal', 'IfcFlowController', 'IfcFlowFitting',
    'IfcFlowSegment', 'IfcFlowMovingDevice', 'IfcFlowStorageDevice',
    'IfcFlowTreatmentDevice', 'IfcEnergyConversionDevice',
    'IfcBoiler', 'IfcChiller', 'IfcPump', 'IfcValve', 'IfcFan',
    'IfcCompressor', 'IfcHeatExchanger', 'IfcAirTerminal',
    'IfcAirTerminalBox', 'IfcCoil', 'IfcCondenser', 'IfcDamper',
    'IfcFilter', 'IfcHumidifier', 'IfcSensor', 'IfcActuator',
    'IfcController', 'IfcAlarm', 'IfcSanitaryTerminal',
    'IfcFireSuppressionTerminal', 'IfcElectricDistributionBoard',
    'IfcTransformer', 'IfcLamp', 'IfcLightFixture',
    'IfcCommunicationsAppliance', 'IfcElectricAppliance',
    'IfcMotor', 'IfcSwitchingDevice', 'IfcProtectiveDevice',
    'IfcDistributionElement', 'IfcDistributionControlElement',
    'IfcDistributionFlowElement',
  ],
};

const CLASS_TO_DISCIPLINE = (() => {
  const out = {};
  for (const [disc, classes] of Object.entries(DISCIPLINE_MAPPING)) {
    for (const cls of classes) out[cls] = disc;
  }
  return out;
})();

const DISCIPLINE_COLOR = {
  MEP:          '#BA7517',
  Architecture: '#3B6D11',
  Structure:    '#185FA5',
  Unclassified: '#888780',
};

const STATUS_COLOR = {
  PASS: '#2E7D32',
  WARN: '#E65100',
  FAIL: '#C62828',
};

const SEVERITY_COLOR = {
  critical: '#C62828',
  major:    '#E65100',
  minor:    '#888780',
};

// FM parameter names — checked exactly as written against
// ``issues[]`` text via case-insensitive substring match.
const FM_PARAMETERS = [
  'AssetID', 'SystemType', 'MaintenanceZone', 'InstallationDate',
  'WarrantyExpiry', 'Manufacturer', 'ModelReference',
];

const stripIfcPrefix = (cls) =>
  typeof cls === 'string' && cls.startsWith('Ifc') ? cls.slice(3) : (cls || '');

const disciplineOf = (cls) => CLASS_TO_DISCIPLINE[cls] || 'Unclassified';
const normalizeDiscipline = (discipline) => discipline === 'Unknown' ? 'Unclassified' : discipline;

const safePct = (num, den) => (
  Number.isFinite(num) && Number.isFinite(den) && den > 0
    ? Math.round((num / den) * 100)
    : null
);


// ═══════════════════════════════════════════════════════════════════
// Section 1 — Failures by IFC class
// ═══════════════════════════════════════════════════════════════════

function FailuresByIfcClass({ rows, agg }) {
  const breakdown = useMemo(() => {
    // Preferred path: backend pre-computed aggregates (full dataset,
    // available in <1s regardless of model size).
    if (agg?.by_ifc_class) {
      return agg.by_ifc_class
        .map(s => ({
          ifcClass: s.ifc_class,
          pass: s.pass, warn: s.warn, fail: s.fail, total: s.total,
          discipline: disciplineOf(s.ifc_class),
          label: stripIfcPrefix(s.ifc_class),
        }))
        .sort((a, b) => (b.fail - a.fail) || (b.total - a.total))
        .slice(0, 10);
    }
    // Fallback: compute from the streamed AIM rows (also used for the
    // category drill-down, where ``agg`` is intentionally null).
    const m = new Map();
    for (const r of rows || []) {
      const cls = r.ifcClass || '';
      if (!cls) continue;
      const slot = m.get(cls) || { ifcClass: cls, pass: 0, warn: 0, fail: 0 };
      const st = (r.overallStatus || '').toUpperCase();
      if (st === 'FAIL') slot.fail += 1;
      else if (st === 'WARN') slot.warn += 1;
      else if (st === 'PASS') slot.pass += 1;
      m.set(cls, slot);
    }
    const arr = Array.from(m.values()).map(s => ({
      ...s,
      total: s.pass + s.warn + s.fail,
      discipline: disciplineOf(s.ifcClass),
      label: stripIfcPrefix(s.ifcClass),
    }));
    arr.sort((a, b) => (b.fail - a.fail) || (b.total - a.total));
    return arr.slice(0, 10);
  }, [rows, agg]);

  if (breakdown.length === 0) {
    return (
      <Card data-testid="ifc-section-failures-by-class">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Failures by IFC class</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground">
          No element rows to chart yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="ifc-section-failures-by-class">
      <CardHeader className="pb-2">
        <CardTitle
          className="text-sm flex items-center gap-2"
          title="% of elements in this class with a PASS/WARN/FAIL verdict that failed. Review and Not Assessed excluded."
        >
          Failures by IFC class
          <span className="text-[10px] text-muted-foreground font-normal">
            top 10 by fail count
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {breakdown.map((row) => {
          const failColor = DISCIPLINE_COLOR[row.discipline];
          const passW = safePct(row.pass, row.total);
          const warnW = safePct(row.warn, row.total);
          const failW = safePct(row.fail, row.total);
          return (
            <div key={row.ifcClass}
                 className="flex items-center gap-2 text-xs"
                 data-testid={`ifc-row-${row.ifcClass}`}>
              <span className="font-medium text-foreground"
                    style={{ width: 130, minWidth: 130 }}
                    title={row.ifcClass}>
                {row.label}
              </span>
              <div className="flex-1 h-5 flex rounded overflow-hidden bg-muted/30"
                   title={`PASS ${row.pass} · WARN ${row.warn} · FAIL ${row.fail}`}>
                {passW !== null && <div style={{ width: `${passW}%`, background: STATUS_COLOR.PASS }} />}
                {warnW !== null && <div style={{ width: `${warnW}%`, background: STATUS_COLOR.WARN }} />}
                {failW !== null && <div style={{ width: `${failW}%`, background: failColor }} />}
              </div>
              <span className="text-right tabular-nums"
                    style={{ width: 90, minWidth: 90 }}>
                <span className="font-semibold text-foreground">{row.fail}</span>
                <span className="text-muted-foreground">
                  {' '}/ {safePct(row.fail, row.total) ?? 'Unavailable'}
                </span>
              </span>
            </div>
          );
        })}
        <Legend />
      </CardContent>
    </Card>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-3 text-[10px] text-muted-foreground pt-1 mt-1 border-t border-border">
      <LegendDot color={STATUS_COLOR.PASS} label="pass" />
      <LegendDot color={STATUS_COLOR.WARN} label="warn" />
      <LegendDot color={DISCIPLINE_COLOR.MEP} label="fail · MEP" />
      <LegendDot color={DISCIPLINE_COLOR.Architecture} label="fail · arch" />
      <LegendDot color={DISCIPLINE_COLOR.Structure} label="fail · struct" />
      <LegendDot color={DISCIPLINE_COLOR.Unclassified} label="fail · unclassified" />
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-block w-2 h-2 rounded" style={{ background: color }} />
      {label}
    </span>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Section 2 — Results by rule
// ═══════════════════════════════════════════════════════════════════

function ResultsByRule({ auditId, agg }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const fetchAllRuleRows = useCallback(async () => {
    if (!auditId) return;
    setLoading(true); setError('');
    const PAGE = 5000;       // endpoint cap (audits_views.py:302)
    const MAX_PAGES = 20;    // safety stop (100k rows ceiling)
    const acc = new Map();
    try {
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const offset = page * PAGE;
        const resp = await apiFetch(
          `${API_URL}/api/audits/${auditId}/validation-details` +
          `?category=custom_bep_rules&limit=${PAGE}&offset=${offset}`,
        );
        if (!resp.ok) {
          throw new Error(`validation-details failed (${resp.status})`);
        }
        const json = await resp.json();
        const rowsPage = json.results || [];
        for (const r of rowsPage) {
          const key = `${r.rule_id || ''}::${r.rule_name || ''}::${r.severity || ''}::${r.ifc_class || ''}`;
          const slot = acc.get(key) || {
            rule_id:    r.rule_id    || '',
            rule_name:  r.rule_name  || r.rule_id || '(unnamed rule)',
            severity:   (r.severity  || 'major').toLowerCase(),
            ifc_class:  r.ifc_class  || '',
            in_scope: 0, pass: 0, fail: 0,
          };
          slot.in_scope += 1;
          const st = (r.status || '').toUpperCase();
          if (st === 'PASS') slot.pass += 1;
          else if (st === 'FAIL') slot.fail += 1;
          acc.set(key, slot);
        }
        if (!json.has_more) break;
      }
      const list = Array.from(acc.values())
        .map(r => ({ ...r, fail_pct: r.in_scope > 0 ? Math.round((r.fail / r.in_scope) * 100) : 0 }))
        .sort((a, b) => b.fail - a.fail);
      setRows(list);
    } catch (e) {
      setError(e.message || 'Failed to load rule results');
    } finally {
      setLoading(false);
    }
  }, [auditId]);

  useEffect(() => {
    // Sep 2026 fix — prefer the backend's pre-computed, exact aggregate
    // (chart_aggregates.py: chunked scan with a known row count, so it
    // isn't subject to PostgREST's 1,000-row-per-request cap). Only fall
    // back to client-side row-paging when the aggregate isn't available
    // yet (e.g. audit still processing).
    if (agg?.results_by_rule) {
      setRows(agg.results_by_rule.map(r => ({
        ...r, severity: (r.severity || 'major').toLowerCase(),
      })));
      setError('');
      setLoading(false);
      return;
    }
    fetchAllRuleRows();
  }, [agg, fetchAllRuleRows]);

  return (
    <Card data-testid="ifc-section-top-rules" className="md:col-span-2">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          Results by rule
          <span className="text-[10px] text-muted-foreground font-normal">
            {rows.length} row{rows.length === 1 ? '' : 's'}
          </span>
        </CardTitle>
        <p className="text-[11px] text-muted-foreground">
          In scope = elements of this class the rule was evaluated on.
          Elements a rule did not apply to are not counted anywhere on this page.
        </p>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
            <Loader2 className="h-3 w-3 animate-spin" />
            Aggregating rule results…
          </div>
        )}
        {error && !loading && (
          <div className="text-xs text-[hsl(var(--destructive))] flex items-center gap-1.5">
            <AlertCircle className="h-3 w-3" /> {error}
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No custom BEP rule results recorded for this audit.
          </p>
        )}
        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-1.5 pr-3 font-medium">Rule</th>
                  <th className="py-1.5 pr-3 font-medium">IFC class</th>
                  <th className="py-1.5 pr-3 font-medium text-right">In scope</th>
                  <th className="py-1.5 pr-3 font-medium text-right">Pass</th>
                  <th className="py-1.5 pr-3 font-medium text-right">Fail</th>
                  <th className="py-1.5 pr-3 font-medium text-right">Fail %</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const dotColor = SEVERITY_COLOR[r.severity] || SEVERITY_COLOR.major;
                  return (
                    <tr key={`${r.rule_id}-${r.ifc_class}`}
                        className="border-b border-border/50"
                        data-testid={`ifc-rule-${r.rule_id}-${r.ifc_class}`}>
                      <td className="py-1 pr-3 font-medium text-foreground" title={r.rule_name}>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="inline-block w-2 h-2 rounded-full shrink-0"
                                style={{ background: dotColor }} title={r.severity} />
                          {r.rule_name}
                        </span>
                      </td>
                      <td className="py-1 pr-3 text-muted-foreground" title={r.ifc_class}>
                        {stripIfcPrefix(r.ifc_class)}
                      </td>
                      <td className="py-1 pr-3 text-right tabular-nums">
                        {r.in_scope.toLocaleString()}
                      </td>
                      <td className="py-1 pr-3 text-right tabular-nums text-[hsl(var(--success))]">
                        {r.pass.toLocaleString()}
                      </td>
                      <td className="py-1 pr-3 text-right tabular-nums text-[hsl(var(--destructive))]">
                        {r.fail.toLocaleString()}
                      </td>
                      <td className="py-1 pr-3 text-right tabular-nums font-semibold text-foreground">
                        {r.fail_pct}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Section 3 — Compliance by discipline
// ═══════════════════════════════════════════════════════════════════

function ComplianceByDiscipline({ rows, agg }) {
  const buckets = useMemo(() => {
    const ORDER = ['MEP', 'Architecture', 'Structure', 'Unclassified'];
    // Preferred path: backend pre-computed aggregates.
    if (agg?.by_discipline) {
      return ORDER.map(d => {
        const source = agg.by_discipline.find(x => normalizeDiscipline(x.discipline) === d);
        const b = source
          ? { ...source, discipline: d }
          : { discipline: d, total: 0, not_assessed: 0, is_mismatch: false };
        const assessed = Number.isFinite(b.total) && Number.isFinite(b.not_assessed)
          ? Math.max(0, b.total - b.not_assessed)
          : null;
        return { ...b, assessed, pct: safePct(assessed, b.total) };
      });
    }
    const m = new Map(ORDER.map(d => [d, { discipline: d, total: 0, not_assessed: 0 }]));
    for (const r of rows || []) {
      const disc = disciplineOf(r.ifcClass);
      const slot = m.get(disc) || { discipline: disc, total: 0, not_assessed: 0 };
      slot.total += 1;
      if ((r.overallStatus || '').toUpperCase() === 'NOT_ASSESSED') slot.not_assessed += 1;
      m.set(disc, slot);
    }
    return ORDER.map(d => {
      const b = m.get(d);
      const assessed = b.total - b.not_assessed;
      return { ...b, assessed, pct: safePct(assessed, b.total) };
    });
  }, [rows, agg]);

  const withElements = buckets.filter(b => b.total > 0);
  const dominant = withElements.reduce(
    (top, b) => (top === null || b.total > top.total) ? b : top,
    null,
  );
  const declared = agg?.declared_discipline;
  const declaredMismatch = declared && dominant && declared !== dominant.discipline;

  return (
    <Card data-testid="ifc-section-compliance">
      <CardHeader className="pb-2">
        <CardTitle
          className="text-sm"
          title="Discipline is inferred from each element's IFC class. Coverage = assessed elements ÷ all elements in the bucket."
        >
          Element assessment coverage
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {buckets.map((b) => {
          const base = DISCIPLINE_COLOR[b.discipline];
          const mismatch = !!b.is_mismatch;
          // Mismatched buckets get a distinct amber hatch instead of the
          // discipline's normal same-weight color fill — a flagged bucket
          // should never look like a normal compliance bar.
          const fillStyle = mismatch
            ? {
                background: 'repeating-linear-gradient(45deg, #E6510033, #E6510033 4px, #E6510014 4px, #E6510014 8px)',
                borderRight: b.pct !== null && b.pct < 100 ? '2px dashed #E65100' : 'none',
                width: `${Math.max(b.pct || 0, b.total > 0 ? 4 : 0)}%`,
              }
            : {
                background: `${base}4D`,
                borderRight: b.pct !== null && b.pct < 100 ? `2px solid ${base}` : 'none',
                width: `${Math.max(b.pct || 0, b.total > 0 ? 4 : 0)}%`,
              };
          return (
            <div key={b.discipline}
                 className="flex items-center gap-2 text-xs"
                 data-testid={`ifc-discipline-${b.discipline}`}>
              <span className="font-medium text-foreground flex items-center gap-1"
                    style={{ width: 90, minWidth: 90 }}>
                {b.discipline}
                {mismatch && (
                  <span className="text-[9px] font-semibold text-amber-600"
                        title={`${b.mismatched} element(s) here don't match the file's declared discipline`}
                        data-testid={`ifc-discipline-${b.discipline}-mismatch-badge`}>
                    ⚠
                  </span>
                )}
              </span>
              <div className="flex-1 h-5 rounded bg-muted/20 overflow-hidden relative">
                <div className="h-full" style={fillStyle} />
                <span className="absolute inset-0 flex items-center pl-2 text-[10px] font-semibold"
                      style={{ color: mismatch ? '#E65100' : base }}>
                  {b.total > 0 ? (b.assessed === 0 ? 'Not assessed' : `${b.pct ?? 'Unavailable'}%`) : 'Unavailable'}
                </span>
              </div>
              <span className="text-right tabular-nums text-muted-foreground"
                    style={{ width: 90, minWidth: 90 }}>
                {Number.isFinite(b.total) ? `${b.total.toLocaleString()} el.` : 'Unavailable'}
              </span>
            </div>
          );
        })}
        {declaredMismatch && (
          <p className="text-[11px] text-amber-600 pt-1.5 mt-1.5 border-t border-border"
             data-testid="ifc-discipline-declared-mismatch">
            This file's declared discipline is{' '}
            <span className="font-semibold">{declared}</span>, not{' '}
            <span className="font-semibold">{dominant.discipline}</span>.
            The breakdown above is a per-element IFC-class guess, not a
            re-classification of the file.
          </p>
        )}
      </CardContent>
    </Card>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Section 4 — FM parameter gap matrix (heatmap)
// ═══════════════════════════════════════════════════════════════════

function FMParameterMatrix({ rows, agg, profile }) {
  const { storeys, matrix, maxValue } = useMemo(() => {
    // Filter to MEP elements only.
    const mep = (rows || []).filter(r => disciplineOf(r.ifcClass) === 'MEP');

    // Per-storey, per-parameter count of elements whose issues contain
    // the parameter name (case-insensitive substring).
    const m = new Map();      // storey → { paramName → count }
    const totals = new Map(); // storey → grand-total
    for (const r of mep) {
      const storey = (r.storey || '').trim() || 'Unassigned';
      const issues = Array.isArray(r.issues) ? r.issues.map(s => String(s).toLowerCase()) : [];
      if (!m.has(storey)) m.set(storey, {});
      const slot = m.get(storey);
      for (const param of FM_PARAMETERS) {
        const needle = param.toLowerCase();
        if (issues.some(s => s.includes(needle))) {
          slot[param] = (slot[param] || 0) + 1;
          totals.set(storey, (totals.get(storey) || 0) + 1);
        }
      }
    }

    // Top 8 storeys by total.
    const topStoreys = Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([k]) => k);

    let max = 0;
    for (const s of topStoreys) {
      for (const p of FM_PARAMETERS) {
        const v = (m.get(s) || {})[p] || 0;
        if (v > max) max = v;
      }
    }
    return { storeys: topStoreys, matrix: m, maxValue: max };
  }, [rows, agg]);

  const cellColor = useCallback((v) => {
    if (v === 0 || maxValue === 0) return { bg: '#f5f5f4', fg: '#666' };
    const ratio = v / maxValue;
    if (ratio < 0.25) return { bg: '#FFF8E1', fg: '#666' };
    if (ratio < 0.50) return { bg: '#FFCC80', fg: '#3b2300' };
    if (ratio < 0.75) return { bg: '#EF6C00', fg: '#fff' };
    return                    { bg: '#B71C1C', fg: '#fff' };
  }, [maxValue]);

  if (profile?.fm_parameters === false) {
    return (
      <Card data-testid="ifc-section-fm-matrix">
        <CardHeader className="pb-2"><CardTitle className="text-sm">FM parameter gap matrix</CardTitle></CardHeader>
        <CardContent className="text-xs text-muted-foreground">Not run — FM parameters were disabled for this audit.</CardContent>
      </Card>
    );
  }
  if (storeys.length === 0) {
    return (
      <Card data-testid="ifc-section-fm-matrix">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">FM parameter gap matrix</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground">
          No FM-parameter issues detected on MEP elements in this audit.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="ifc-section-fm-matrix">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          FM parameter gap matrix
          <span className="text-[10px] text-muted-foreground font-normal">
            MEP only · top {storeys.length} storeys
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="text-[11px] border-collapse">
          <thead>
            <tr>
              <th className="text-left pr-3 pb-1 font-semibold sticky left-0 bg-card">
                Storey
              </th>
              {FM_PARAMETERS.map((p) => (
                <th key={p}
                    className="px-2 pb-1 font-medium text-muted-foreground"
                    style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                             minWidth: 30 }}>
                  {p}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {storeys.map((s) => {
              const slot = matrix.get(s) || {};
              return (
                <tr key={s} data-testid={`ifc-fm-row-${s}`}>
                  <td className="font-medium text-foreground pr-3 py-0.5 sticky left-0 bg-card whitespace-nowrap">
                    {s}
                  </td>
                  {FM_PARAMETERS.map((p) => {
                    const v = slot[p] || 0;
                    const { bg, fg } = cellColor(v);
                    return (
                      <td key={p}
                          className="text-center font-semibold tabular-nums"
                          style={{ background: bg, color: fg, padding: '4px 2px',
                                   minWidth: 30, border: '1px solid #fff' }}
                          title={`${p} on ${s}: ${v} MEP elements missing this parameter`}>
                        {v || ''}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="text-[10px] text-muted-foreground mt-2">
          cell = MEP elements missing parameter on that floor
        </p>
      </CardContent>
    </Card>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Default export — composes the four sections.
// ═══════════════════════════════════════════════════════════════════

export default function IFCValidationPanel({ data, summary, auditId, aggregates, profile }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6"
         data-testid="ifc-validation-panel">
      <FailuresByIfcClass rows={data} agg={aggregates} />
      <ResultsByRule auditId={auditId} agg={aggregates} />
      <ComplianceByDiscipline rows={data} agg={aggregates} />
      <FMParameterMatrix rows={data} agg={aggregates} summary={summary} profile={profile} />
    </div>
  );
}
